import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import type { Recommendation, ApiStatus, TickerInfo, AuthMode, FiltersState } from './types';
import { getFiltersFromSearch, filtersToSearch } from './utils/filters';
import { Header, AuthView, RecommendationFilters, RecommendationList, StatsGrid, LandingPage } from './components';
import { AboutPage } from './pages/AboutPage';
import { FeaturesPage } from './pages/FeaturesPage';
import { PricingPage } from './pages/PricingPage';
import { BlogPage } from './pages/BlogPage';
import { FAQPage } from './pages/FAQPage';
import './App.css';

function App() {
  const [authToken, setAuthToken] = useState<string | null>(() => localStorage.getItem('auth_token'));
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authNewPassword, setAuthNewPassword] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);
  const location = useLocation();

  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [status, setStatus] = useState<ApiStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRecommendationIds, setExpandedRecommendationIds] = useState<Set<number>>(() => new Set());
  const [filters, setFilters] = useState<FiltersState>(() =>
    getFiltersFromSearch(typeof window !== 'undefined' ? window.location.search : '')
  );

  const [tickerSearch, setTickerSearch] = useState('');
  const [allTickers, setAllTickers] = useState<TickerInfo[]>([]);
  const [showTickerDropdown, setShowTickerDropdown] = useState(false);
  const hasQuery = Boolean(filters.ticker || filters.recommendation_type || filters.has_trade || tickerSearch);

  // Keep URL in sync with filters so links can be shared
  useEffect(() => {
    if (!authToken) return;
    const query = filtersToSearch(filters);
    const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    if (window.location.search !== (query ? `?${query}` : '')) {
      window.history.replaceState(null, '', url);
    }
  }, [authToken, filters]);

  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [authToken]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem('auth_token');
          setAuthToken(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    if (!authToken) return;
    const fetchTickers = async () => {
      try {
        const response = await axios.get('/tickers');
        setAllTickers(response.data);
      } catch (err) {
        console.error('Error fetching tickers:', err);
      }
    };
    fetchTickers();
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;
    const pollStatus = async () => {
      try {
        const response = await axios.get('/status');
        setStatus(response.data);
      } catch (err) {
        console.error('Error fetching status:', err);
      }
    };
    pollStatus();
    const interval = setInterval(pollStatus, 30000);
    return () => clearInterval(interval);
  }, [authToken]);

  const fetchRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filters.ticker) params.append('ticker', filters.ticker);
      if (filters.recommendation_type) params.append('recommendation_type', filters.recommendation_type);
      if (filters.has_trade) params.append('has_trade', 'true');
      params.append('sort_by', filters.sort_by);
      params.append('limit', filters.limit.toString());
      const response = await axios.get<Recommendation[]>(`/recommendations?${params}`);
      setRecommendations(response.data);
    } catch (err) {
      setError('Failed to fetch recommendations. Please try again.');
      console.error('Error fetching recommendations:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (authToken) fetchRecommendations();
  }, [authToken, fetchRecommendations]);

  const filteredTickers = useMemo(() => {
    if (!tickerSearch) return [];
    const search = tickerSearch.toUpperCase();
    return allTickers
      .filter(t =>
        t.ticker.includes(search) ||
        t.company_name.toUpperCase().includes(search)
      )
      .slice(0, 10);
  }, [tickerSearch, allTickers]);

  const handleTickerSelect = (ticker: string) => {
    setFilters(prev => ({ ...prev, ticker }));
    setTickerSearch('');
    setShowTickerDropdown(false);
  };

  const handleFilterChange = (key: keyof FiltersState, value: string | number | boolean) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleExpanded = (id: number) => {
    setExpandedRecommendationIds(prev => {
      if (prev.has(id)) return new Set();
      return new Set([id]);
    });
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setAuthToken(null);
    setStatus(null);
    setRecommendations([]);
    setAllTickers([]);
    setAuthMode('login');
  };

  const resetAuthMessages = () => {
    setAuthError(null);
    setAuthMessage(null);
  };

  const setAuthModeAndReset = (mode: AuthMode) => {
    resetAuthMessages();
    setAuthMode(mode);
  };

  const openAuth = (mode: AuthMode = 'signup') => {
    setAuthModeAndReset(mode);
    setShowAuthOverlay(true);
  };

  const closeAuth = () => {
    resetAuthMessages();
    setShowAuthOverlay(false);
  };

  useEffect(() => {
    if (!showAuthOverlay) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAuthError(null);
        setAuthMessage(null);
        setShowAuthOverlay(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showAuthOverlay]);

  const authOverlay = showAuthOverlay ? (
    <div className="auth-overlay" role="dialog" aria-modal="true">
      <div className="auth-overlay__backdrop" onClick={closeAuth} />
      <div className="auth-overlay__panel">
        <button
          type="button"
          className="auth-overlay__close"
          aria-label="Close authentication dialog"
          onClick={closeAuth}
        >
          &times;
        </button>
        <AuthView
          mode={authMode}
          email={authEmail}
          password={authPassword}
          newPassword={authNewPassword}
          code={authCode}
          error={authError}
          message={authMessage}
          onModeChange={setAuthModeAndReset}
          onEmailChange={setAuthEmail}
          onPasswordChange={setAuthPassword}
          onNewPasswordChange={setAuthNewPassword}
          onCodeChange={setAuthCode}
          onLogin={() => login()}
          onSignup={() => signup()}
          onVerify={() => verifyAccount()}
          onRequestVerification={() => requestVerification()}
          onRequestPasswordReset={() => requestPasswordReset()}
          onResetPassword={() => resetPassword()}
        />
      </div>
    </div>
  ) : null;

  const login = async () => {
    resetAuthMessages();
    try {
      const resp = await axios.post('/auth/login', { email: authEmail, password: authPassword });
      const token = resp?.data?.access_token as string | undefined;
      if (!token) throw new Error('Missing token');
      localStorage.setItem('auth_token', token);
      setAuthToken(token);
      setAuthPassword('');
      setShowAuthOverlay(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setAuthError(err?.response?.data?.detail || 'Login failed');
    }
  };

  const signup = async () => {
    resetAuthMessages();
    try {
      const resp = await axios.post('/auth/signup', { email: authEmail, password: authPassword });
      const serverMsg = (resp?.data as { message?: string })?.message;
      setAuthMessage(serverMsg || 'Account created. Check your email for verification.');
      setAuthMode('verify');
      setAuthPassword('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setAuthError(err?.response?.data?.detail || 'Signup failed');
    }
  };

  const requestVerification = async () => {
    resetAuthMessages();
    try {
      const resp = await axios.post('/auth/request-verification', { email: authEmail });
      const serverMsg = (resp?.data as { message?: string })?.message;
      setAuthMessage(serverMsg || 'Verification email sent if the account exists.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setAuthError(err?.response?.data?.detail || 'Failed to request verification');
    }
  };

  const verifyAccount = async () => {
    resetAuthMessages();
    try {
      await axios.post('/auth/verify', { token: authCode });
      setAuthMessage('Account verified. You can now log in.');
      setAuthMode('login');
      setAuthCode('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setAuthError(err?.response?.data?.detail || 'Verification failed');
    }
  };

  const requestPasswordReset = async () => {
    resetAuthMessages();
    try {
      const resp = await axios.post('/auth/request-password-reset', { email: authEmail });
      const serverMsg = (resp?.data as { message?: string })?.message;
      setAuthMessage(serverMsg || 'Password reset email sent if the account exists.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setAuthError(err?.response?.data?.detail || 'Failed to request password reset');
    }
  };

  // Deep-link handlers for verification / reset links
  useEffect(() => {
    if (authToken) return;
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token') || '';
    const emailParam = params.get('email') || '';
    if (!tokenParam) return;

    if (location.pathname.startsWith('/reset-password')) {
      setAuthModeAndReset('forgot');
      setAuthCode(tokenParam);
      if (emailParam) setAuthEmail(emailParam);
      setShowAuthOverlay(true);
    } else if (location.pathname.startsWith('/verify')) {
      setAuthModeAndReset('verify');
      setAuthCode(tokenParam);
      if (emailParam) setAuthEmail(emailParam);
      setShowAuthOverlay(true);
    }
  }, [location.pathname, location.search, authToken]);

  const resetPassword = async () => {
    resetAuthMessages();
    try {
      await axios.post('/auth/reset-password', { token: authCode, new_password: authNewPassword });
      setAuthMessage('Password updated. You can now log in.');
      setAuthMode('login');
      setAuthCode('');
      setAuthNewPassword('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setAuthError(err?.response?.data?.detail || 'Password reset failed');
    }
  };

  if (!authToken) {
    return (
      <>
        <Routes>
          <Route
            path="/"
            element={
              <LandingPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />
            }
          />
          <Route
            path="/reset-password"
            element={
              <LandingPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />
            }
          />
          <Route
            path="/verify"
            element={
              <LandingPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />
            }
          />
          <Route
            path="/about"
            element={<AboutPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />}
          />
          <Route
            path="/features"
            element={<FeaturesPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />}
          />
          <Route
            path="/pricing"
            element={<PricingPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />}
          />
          <Route
            path="/blog"
            element={<BlogPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />}
          />
          <Route
            path="/faq"
            element={<FAQPage onPrimaryCta={() => openAuth('signup')} onSecondaryCta={() => openAuth('login')} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {authOverlay}
      </>
    );
  }

  return (
    <div className="app">
      <Header onLogout={logout} />
      <main className="main">
        <div className="container">
          {status && <StatsGrid status={status} />}
          <RecommendationFilters
            filters={filters}
            tickerSearch={tickerSearch}
            showTickerDropdown={showTickerDropdown}
            filteredTickers={filteredTickers}
            onTickerSearchChange={(v) => {
              setTickerSearch(v);
              setShowTickerDropdown(true);
            }}
            onTickerDropdownFocus={() => setShowTickerDropdown(true)}
            onTickerDropdownBlur={() => setTimeout(() => setShowTickerDropdown(false), 200)}
            onTickerSelect={handleTickerSelect}
            onFilterChange={handleFilterChange}
          />
          <RecommendationList
            recommendations={recommendations}
            expandedIds={expandedRecommendationIds}
            loading={loading}
            error={error}
            hasQuery={hasQuery}
            onToggleExpand={toggleExpanded}
            onRetry={fetchRecommendations}
          />
        </div>
      </main>
    </div>
  );
}

export default App;
